import { createContext, FC, useState } from 'react';

export type User = {
    id: number;
    name: string;
    email: string;
    phone: string;
};

export type Organization = {
    id: number;
    name: string;
    code: string;
    address: string;
    latitude: number;
    longitude: number;
};

export type ServiceGroup = {
    id: number;
    name: string;
    isServiceGroupCollapsed: boolean;
    attributes: Attribute[];
};

export type AttributeValue = {
    key: string;
    name: string;
};

export type Attribute = {
    id: number;
    variable: boolean;
    code: string;
    order: number;
    datatype: string;
    required: boolean;
    description: string;
    values: AttributeValue[];
};

export type BoundaryPoint = [number, number];

export type AttachmentSettings = {
    accept: string;
    maxSize: number;
    maxNumberOfFiles: number;
};

export type Service = {
    tag: string;
    serviceName: string;
    serviceCode: string;
    isMetadata: boolean;
    type: string;
    description: string;
    descriptionHTML: string;
    isPrivate: boolean;
    keywords: string;
    group: string;
    isNoLocation: boolean;
    phone: string;
    sms: string;
    link: string;
    email: string;
    isIgnoreFiles: boolean;
    serviceGroups: ServiceGroup[];
    attributes: Attribute[];
};

export type RequestSettings = {
    tabsSort: string[];
    enabledTabs: string[];
};

export type StyleVariables = {
    [key: string]: string;
};

export type RequestTheme = {
    [key: string]: StyleVariables;
};

export type PresetLocation = {
    name: string | null;
    email: string | null;
    address: string;
    address2: string | null;
    phone: string | null;
    latitude: number;
    longitude: number;
};

export type Jurisdiction = {
    slug: string;
    wkt: string;
    boundaryPoints: BoundaryPoint[];
    code: string;
    title: string;
    requestMethod: string;
    requestFormStyle: string;
    requestTitle: string;
    requestMessageHTML: string;
    requestLogoUrl: string;
    requestUrl: string;
    requestLocationIssueTitle: string;
    requestTheme: RequestTheme;
    isRequestAttachments: boolean;
    isHideTopLocationInfo: boolean;
    isRequestPasswordProtected: boolean;
    isRequestPhoneVerification: boolean;
    isRequestEmailVerification: boolean;
    requestSettings: RequestSettings,
    address: string;
    addressLat: number | null;
    addressLng: number | null;
    noLocationAddress: string;
    noLocationAddressLat: number | null;
    noLocationAddressLng: number | null;
    requestShowNameEntry: string;
    isSubjurisdictionCoordsDefaultAddress: boolean;
    isEmailVerify: boolean;
    isPhoneVerify: boolean;
    isNameEntry: boolean;
    isHideDuplicateMap: boolean;
    isRequestAnonymous: boolean;
    isRequestConfidential: boolean;
    isRequestPrivate: boolean;
    isNewRequestRequireAdditionalComments: boolean;
    isNewRequestRecaptchaVerify: boolean;
    recaptchaSiteKey: string;
    googleMapKey: string;
    serviceMode: string;
    organizations: Organization[];
    services: Service[];
    user: User | null;
    attachmentSettings: AttachmentSettings;
    presetLocation: PresetLocation | null;
    isRequestHideHeadNav: boolean;
};

export const JurisdictionContext = createContext<Jurisdiction>({} as Jurisdiction);

export const JurisdictionContextProvider: FC = ({ children }) => {
    const [jurisdiction] = useState<Jurisdiction>(window._jurisdiction || {});

    return (
        <JurisdictionContext.Provider value={jurisdiction}>
            {children}
        </JurisdictionContext.Provider>
    );
};
